import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, Eye, AlertTriangle, PackageCheck, Plus, ArrowRight,
  Clock, ChevronDown, ChevronUp, Building2, DollarSign, UserPlus,
} from 'lucide-react';

const API = '/api';

// Systems Watchlist — full shape (Randall 2026-07-28): holder/contact, facility,
// channel/partner, billing, decision, dated touch history, next step + nudge date.
// "keep" -> Onboard Fitter (license flow, link only); "return" -> Returns Inbox.

const DECISIONS = [
  { id: 'open', label: 'Open', cls: 'text-blue-300 border-blue-400/40 bg-blue-400/10' },
  { id: 'keep', label: 'Keep', cls: 'text-hg-green border-hg-green/40 bg-hg-green/10' },
  { id: 'return', label: 'Return', cls: 'text-amber-300 border-amber-400/40 bg-amber-400/10' },
  { id: 'transfer', label: 'Transfer', cls: 'text-purple-300 border-purple-400/40 bg-purple-400/10' },
  { id: 'unresponsive', label: 'Unresponsive', cls: 'text-red-400 border-red-500/40 bg-red-500/10' },
];

function Entry({ e, onChanged, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(e.notes || '');
  const [nextStep, setNextStep] = useState(e.next_step || '');
  const [nudge, setNudge] = useState(e.nudge_date || '');
  const [touchNote, setTouchNote] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const d = DECISIONS.find((x) => x.id === e.decision) || DECISIONS[0];
  const b = e.billing || {};
  const dirty = notes !== (e.notes || '') || nextStep !== (e.next_step || '') || nudge !== (e.nudge_date || '');
  const nudgeDue = e.nudge_date && e.nudge_date <= new Date().toISOString().slice(0, 10) && e.decision === 'open';

  const put = async (patch, tag = 'save') => {
    setBusy(tag); setError('');
    try {
      await axios.put(`${API}/systems-watchlist/${e.id}`, patch);
      onChanged?.();
    } catch (err) { setError(err.response?.data?.detail || 'Save failed.'); }
    setBusy('');
  };

  const addTouch = async () => {
    if (!touchNote.trim()) return;
    setBusy('touch'); setError('');
    try {
      await axios.post(`${API}/systems-watchlist/${e.id}/touch`, { note: touchNote.trim() });
      setTouchNote(''); onChanged?.();
    } catch (err) { setError(err.response?.data?.detail || 'Could not add touch.'); }
    setBusy('');
  };

  const startReturn = async () => {
    if (!window.confirm(`Create a Return for ${e.name} in the Returns Inbox? (Drafts the return email; nothing sends.)`)) return;
    setBusy('return'); setError('');
    try {
      await axios.post(`${API}/systems-watchlist/${e.id}/start-return`);
      onChanged?.();
    } catch (err) { setError(err.response?.data?.detail || 'Could not start the return.'); }
    setBusy('');
  };

  return (
    <div className={`bg-hg-surface border rounded-sm ${nudgeDue ? 'border-amber-400/60' : 'border-hg-border'}`}
         data-testid={`watch-${e.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left px-4 py-3 flex items-center gap-2 flex-wrap">
        <span className="text-sm font-sans text-hg-text font-medium">{e.name}</span>
        {e.facility && <span className="text-[11px] font-sans text-hg-text-dim inline-flex items-center gap-1"><Building2 size={10} />{e.facility}{e.location ? ` · ${e.location}` : ''}</span>}
        <span className={`text-[10px] font-sans uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${d.cls}`}>{d.label}</span>
        {e.may_pull && <span className="text-[10px] font-sans uppercase tracking-wider border border-amber-400/40 text-amber-300 rounded-sm px-1.5 py-0.5"><AlertTriangle size={9} className="inline mr-1" />may pull</span>}
        {nudgeDue && <span className="text-[10px] font-sans text-amber-300">nudge due {e.nudge_date}</span>}
        <span className="ml-auto text-[10px] font-sans text-hg-text-dim">{(e.touches || []).length} touches</span>
        {open ? <ChevronUp size={13} className="text-hg-text-dim" /> : <ChevronDown size={13} className="text-hg-text-dim" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-hg-border/50 pt-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px] font-sans text-hg-text-dim">
            <div>{e.email ? <>Contact: <span className="text-hg-text">{e.email}</span></> : 'No contact email'}</div>
            {e.channel && <div>Channel: <span className="text-hg-text">{e.channel}</span>{e.partner_name ? ` (partner: ${e.partner_name}${e.partner_email ? ` <${e.partner_email}>` : ''})` : ''}</div>}
            {(b.fee_monthly || b.paid_by || b.last_payment_date) && (
              <div className="md:col-span-2"><DollarSign size={11} className="inline text-hg-green mr-1" />
                {b.fee_monthly ? `$${b.fee_monthly}/mo` : 'Fee —'} · paid by <span className="text-hg-text">{b.paid_by || '—'}</span>
                {b.last_payment_date && <> · last payment <span className={b.unpaid_since ? 'text-amber-300' : 'text-hg-text'}>{b.last_payment_date}</span></>}
                {b.unpaid_since && <span className="text-amber-300"> · unpaid since</span>}
                {b.note && <span> · {b.note}</span>}
              </div>
            )}
          </div>

          {/* decision */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Decision:</span>
            {DECISIONS.map((opt) => (
              <button key={opt.id} disabled={busy !== '' || e.decision === opt.id}
                onClick={() => put({ decision: opt.id }, 'decision')}
                className={`px-2 py-1 text-[11px] font-sans border rounded-sm ${e.decision === opt.id ? opt.cls : 'border-hg-border text-hg-text-dim hover:text-hg-text'}`}>
                {opt.label}
              </button>
            ))}
            <label className="inline-flex items-center gap-1.5 text-[11px] font-sans text-hg-text-dim cursor-pointer ml-auto">
              <input type="checkbox" checked={!!e.may_pull} disabled={busy !== ''}
                onChange={(ev) => put({ may_pull: ev.target.checked }, 'flag')} />
              may need to pull the system
            </label>
          </div>

          {/* transitions */}
          <div className="flex items-center gap-2 flex-wrap">
            {e.decision === 'keep' && (
              <button onClick={() => onNavigate?.('onboard')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-green/40 text-hg-green rounded-sm hover:bg-hg-green/10">
                <UserPlus size={12} /> Set up account / license agreement <ArrowRight size={11} />
              </button>
            )}
            {e.decision === 'return' && !e.return_id && (
              e.fitter_match ? (
                <button onClick={startReturn} disabled={busy !== ''} data-testid={`watch-start-return-${e.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40">
                  {busy === 'return' ? <Loader2 size={11} className="animate-spin" /> : <PackageCheck size={12} />}
                  Create Return in Returns Inbox
                </button>
              ) : (
                <span className="text-[11px] font-sans text-hg-text-dim italic">no matching fitter record — start from Returns directly</span>
              )
            )}
            {e.return_id && (
              <button onClick={() => onNavigate?.('returns')}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-sans border border-hg-green/40 text-hg-green rounded-sm hover:bg-hg-green/10">
                Open in Returns <ArrowRight size={11} />
              </button>
            )}
          </div>

          {/* touch history */}
          <div className="space-y-1.5">
            <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Touch history</div>
            {(e.touches || []).length === 0 && <div className="text-[11px] font-sans text-hg-text-dim italic">No touches recorded.</div>}
            {(e.touches || []).map((t, i) => (
              <div key={i} className="text-[12px] font-sans text-hg-text bg-hg-bg/60 border-l-2 border-hg-green/50 rounded-sm px-2.5 py-1.5">
                <span className="text-hg-text-dim">{t.at} · {t.by}: </span>{t.note}
              </div>
            ))}
            <div className="flex gap-2">
              <input value={touchNote} onChange={(ev) => setTouchNote(ev.target.value)}
                onKeyDown={(ev) => { if (ev.key === 'Enter') addTouch(); }}
                placeholder="Add a touch (what happened, dated today)…"
                className="flex-1 bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text" />
              <button onClick={addTouch} disabled={busy !== '' || !touchNote.trim()}
                className="px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green hover:border-hg-green disabled:opacity-40">
                {busy === 'touch' ? <Loader2 size={11} className="animate-spin" /> : <Clock size={11} />}
              </button>
            </div>
          </div>

          {/* next step + nudge + notes */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
            <input value={nextStep} onChange={(ev) => setNextStep(ev.target.value)} placeholder="Next step…"
              className="bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text" />
            <input type="date" value={nudge} onChange={(ev) => setNudge(ev.target.value)} title="Nudge date"
              className="bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text" />
          </div>
          <textarea value={notes} onChange={(ev) => setNotes(ev.target.value)} rows={2} placeholder="Notes…"
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text resize-y" />
          {dirty && (
            <button onClick={() => put({ notes, next_step: nextStep, nudge_date: nudge || null })}
              disabled={busy !== ''}
              className="px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40">
              {busy === 'save' ? <Loader2 size={11} className="animate-spin" /> : 'Save'}
            </button>
          )}
          {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
        </div>
      )}
    </div>
  );
}

export default function SystemsWatchlist({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/systems-watchlist`);
      setData(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load the watchlist.'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/systems-watchlist`, { name: newName.trim(), email: newEmail.trim() || null });
      setNewName(''); setNewEmail(''); load();
    } catch (e) { setError(e.response?.data?.detail || 'Could not add.'); }
    setAdding(false);
  };

  const entries = data?.entries || [];
  return (
    <div className="space-y-5" data-testid="systems-watchlist-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">SYSTEMS WATCHLIST</h1>
          <p className="text-sm text-hg-text-dim font-sans mt-1">
            Fitting systems and accounts under review — who has one, how it got there, who's paying,
            and what happens next. "Keep" hands to the license flow; "Return" creates a Return. Nothing here sends email.
          </p>
        </div>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
        </button>
      </div>

      <div className="bg-hg-surface border border-hg-border rounded-sm p-4 flex items-center gap-2 flex-wrap">
        <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name / account"
          className="flex-1 min-w-[180px] bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text" />
        <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Email (optional — enables the Returns handoff)"
          className="flex-1 min-w-[220px] bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text" />
        <button onClick={add} disabled={adding || !newName.trim()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40">
          {adding ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add to watchlist
        </button>
      </div>

      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
      {loading && !data && <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-hg-green" /></div>}

      {data && (entries.length === 0 ? (
        <div className="text-center py-14 space-y-2">
          <Eye size={22} className="mx-auto text-hg-green" />
          <div className="text-sm text-hg-text-dim font-sans">Nothing under review.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => (
            <Entry key={e.id} e={e} onChanged={load} onNavigate={onNavigate} />
          ))}
        </div>
      ))}
    </div>
  );
}
